import { useRef, type MouseEvent } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowUpRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { siteConfig } from '@/config/site';

/**
 * Premium "hero asset" section showcasing the 3D TEF brand mark.
 * - Continuous float + slow rotate on the artwork
 * - Mouse-tracked parallax tilt on desktop (no-op on touch)
 * - Soft teal radial glow + grid backdrop
 */
export function BrandShowcase() {
    const containerRef = useRef<HTMLDivElement>(null);

    // Raw pointer position (-0.5 → 0.5 normalized).
    const px = useMotionValue(0);
    const py = useMotionValue(0);

    // Spring-smoothed values feed into the transforms.
    const sx = useSpring(px, { stiffness: 80, damping: 18, mass: 0.6 });
    const sy = useSpring(py, { stiffness: 80, damping: 18, mass: 0.6 });

    const rotateY = useTransform(sx, [-0.5, 0.5], [-12, 12]);
    const rotateX = useTransform(sy, [-0.5, 0.5], [8, -8]);
    const translateX = useTransform(sx, [-0.5, 0.5], [-14, 14]);
    const translateY = useTransform(sy, [-0.5, 0.5], [-10, 10]);

    function handleMove(e: MouseEvent<HTMLDivElement>) {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        px.set((e.clientX - rect.left) / rect.width - 0.5);
        py.set((e.clientY - rect.top) / rect.height - 0.5);
    }

    function handleLeave() {
        px.set(0);
        py.set(0);
    }

    return (
        <section className="relative overflow-hidden border-y border-border bg-section">
            {/* Background layers */}
            <div className="absolute inset-0 bg-grid opacity-60" />
            <div
                aria-hidden
                className="absolute inset-0 bg-[radial-gradient(ellipse_60%_60%_at_30%_50%,rgba(79,143,139,0.18),transparent_70%)]"
            />
            <div
                aria-hidden
                className="absolute inset-0 bg-[radial-gradient(ellipse_50%_50%_at_85%_50%,rgba(127,174,171,0.12),transparent_70%)]"
            />

            <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
                <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                    {/* ── Artwork ─────────────────────────────────────── */}
                    <div
                        ref={containerRef}
                        onMouseMove={handleMove}
                        onMouseLeave={handleLeave}
                        className="relative order-2 lg:order-1 [perspective:1200px]"
                    >
                        {/* Soft glow puddle behind the mark */}
                        <motion.div
                            aria-hidden
                            className="absolute inset-0 -z-10 rounded-full"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                            style={{
                                background:
                                    'radial-gradient(ellipse 70% 55% at 50% 55%, rgba(79,143,139,0.45), transparent 70%)',
                                filter: 'blur(40px)',
                            }}
                        />

                        {/* Floating artwork */}
                        <motion.div
                            initial={{ opacity: 0, y: 40, scale: 0.92 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                            className="relative mx-auto aspect-square w-full max-w-md lg:max-w-xl"
                            style={{ rotateX, rotateY, x: translateX, y: translateY, transformStyle: 'preserve-3d' }}
                        >
                            {/* Continuous gentle float — independent of pointer parallax */}
                            <motion.div
                                animate={{
                                    y: [0, -14, 0],
                                    rotate: [-1.2, 1.2, -1.2],
                                }}
                                transition={{
                                    duration: 9,
                                    ease: 'easeInOut',
                                    repeat: Infinity,
                                }}
                                className="relative w-full h-full"
                            >
                                <img
                                    src="/images/tef-image.png"
                                    alt={`${siteConfig.brand.shortName} brand mark`}
                                    className="w-full h-full object-contain drop-shadow-[0_30px_60px_rgba(47,95,92,0.45)]"
                                    width={1254}
                                    height={1254}
                                    loading="lazy"
                                />
                            </motion.div>
                        </motion.div>

                        {/* Floating accent dots — subtle motion garnish */}
                        <motion.span
                            aria-hidden
                            className="absolute top-6 right-8 h-2.5 w-2.5 rounded-full bg-primary/70"
                            animate={{ y: [0, -10, 0], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                        />
                        <motion.span
                            aria-hidden
                            className="absolute bottom-12 left-4 h-1.5 w-1.5 rounded-full bg-secondary"
                            animate={{ y: [0, -8, 0], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
                        />
                        <motion.span
                            aria-hidden
                            className="absolute top-1/3 left-2 h-2 w-2 rounded-full bg-primary/40"
                            animate={{ y: [0, -12, 0], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
                        />
                    </div>

                    {/* ── Copy ────────────────────────────────────────── */}
                    <div className="order-1 lg:order-2">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <Badge variant="outline" className="mb-6 border-primary/30 text-primary/90">
                                <Sparkles className="mr-1.5 h-3 w-3" />
                                Built for serious traders
                            </Badge>
                        </motion.div>

                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.65, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.05]"
                        >
                            <span className="block">Backed by</span>
                            <span className="block text-gradient">{siteConfig.brand.name}.</span>
                            <span className="block">Built to last.</span>
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.65, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl"
                        >
                            More than a prop firm — a long-term partner. Trade on{' '}
                            {siteConfig.platform.name}, scale your account on transparent rules, and
                            keep up to <span className="text-foreground font-semibold">80%</span> of every
                            payout. No theatrics. Just precision capital, paid on time.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.65, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-10 grid grid-cols-3 gap-6 max-w-md"
                        >
                            {[
                                { value: '80%', label: 'Profit Split' },
                                { value: '24h', label: 'Avg Payout' },
                                { value: '$100K', label: 'Max Capital' },
                            ].map((s) => (
                                <div key={s.label} className="border-l-2 border-primary/40 pl-4">
                                    <p className="text-2xl font-bold text-foreground">{s.value}</p>
                                    <p className="mt-1 text-xs text-muted-foreground uppercase tracking-wide">{s.label}</p>
                                </div>
                            ))}
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 0.65, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="mt-10 flex flex-col sm:flex-row gap-3"
                        >
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg" className="px-7">
                                    Get Funded
                                    <ArrowUpRight className="ml-2 h-4 w-4" />
                                </Button>
                            </a>
                            <Link to="/about">
                                <Button size="lg" variant="outline" className="px-7">
                                    Our Story
                                </Button>
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
